<template>
  <div class="contents contents--full layout-wrapper">

    <div v-if="showModal" class="modal-overlay">
      <div class="modal-box">
        <h2 class="modal-title">기관 정보 입력</h2>

        <div class="modal-row">
          <label>사업자번호</label>
          <input type="text" v-model="form.businessNumber" placeholder="예: 601-82-03112" />
        </div>

        <div class="modal-row">
          <label>주소</label>
          <input type="text" v-model="form.address" placeholder="주소를 입력하세요" />
        </div>

        <div class="modal-row">
          <label>상임감사</label>
          <input type="text" v-model="form.standingAuditorName" placeholder="상임감사명을 입력하세요" />
        </div>

        <button class="modal-save" @click="saveModal">저장</button>
      </div>
    </div>

    <div class="contents__wrap" v-if="!showModal">
       <div style="text-align: right; margin-bottom: 10px;">
        <button class="cert-download-btn" @click="downloadPdf(state.id)">
          PDF 다운로드 {{ state.downloadCount }} / 3
        </button>
      </div>

      <div class="certificate-page">
        <div class="certificate-card" ref="page1">
          <!-- 상단 타이틀 -->
          <h1 class="cert-title">Internal Audit Quality Certification</h1>

          <!-- 파란 띠 -->
          <div class="cert-blue-bar">
            INTERNAL AUDIT QUALITY ASSESSMENT INTERNAL AUDIT QUALITY ASSESSMENT
            INTERNAL AUDIT QUALITY ASSESSMENT
          </div>

          <!-- Certificate Number -->
          <p class="cert-number">
            <span class="cert-number-text">
              <span class="label">Certificate Number :</span>
              <span class="value">{{ state.issueCode }}</span>
            </span>
          </p>

          <!-- 상단 설명 문단 -->
          <p class="cert-text">
            This is to certify that the Internal Audit Activity of
            <span class="highlight">{{ orgName }}</span>
            has been independently assessed<br>
            in accordance with 
            <em class="curved-bold">the Global Internal Audit Standards</em> 
            and has been certified by the<br>
            Internal Control Institute Korea. As a result of this evaluation, the internal audit quality level<br>
            of <span class="highlight">{{ orgName }}</span> has been determined as follows:
          </p>


          <!-- 메인 영역: 테이블 + 등급 -->
          <div class="cert-main-row">
            <!-- 왼쪽 정보 테이블 -->
            <table class="cert-table">
              <tr>
                <th>Institution</th>
                <td>{{ orgName }}</td>
              </tr>
              <tr>
                <th>Chief Audit Executive</th>
                <td>{{ form.standingAuditorName }}</td>
              </tr>
              <tr>
                <th>Business Registration No.</th>
                <td>{{ form.businessNumber }}</td>
              </tr>
              <tr>
                <th>Address</th>
                <td>{{ form.address }}</td>
              </tr>
              <tr>
                <th>Date of Assessment</th>
                <td>{{ state.evaluationDate }}</td>
              </tr>
              <tr>
                <th>Validity Period</th>
                <td>{{ state.expireDate }}</td>
              </tr>
            </table>

            <!-- 오른쪽 등급 박스 -->
            <div class="cert-grade-box">
              <div class="cert-grade-header">Quality Conformance Grade</div>
              <div class="cert-grade-value">
                {{ domainStandardList[0]?.finalGrade || 'A' }}
                <img src="/QGC/워터마크.png" class="grade-watermark">
              </div>
            </div>
          </div>

          <!-- 중간 타이틀 -->
          <div class="cert-middle">
            <div class="cert-qc-title">Quality Conformance</div>
            <div class="cert-issued">
              Issued : {{ formatDate(state.evaluationDate) }}
            </div>
            <div class="cert-issued-sub">
              (to be renewed within each one year as of the date above)
            </div>
          </div>

          <!-- 본문 설명 -->
          <p class="cert-body-text">
            This certificate confirms that the assessment was conducted based on 
            submitted materials, <br/>
            on-site inspections, and interviews, and reflects
            the level of conformance with international <br/>
            standards as of the evaluation date. The grade remains valid until the end of the
            certification <br/>period and does not cover changes occurring thereafter.
          </p>

          <!-- 하단 로고 + 서명 영역 -->
          <div class="cert-bottom">
            <!-- 1행: 금박딱지 + 중앙 로고 -->
            <div class="cert-bottom-top">
              <div class="cert-seal">
                <img src="/QGC/ICI 인증.png" alt="Gold Seal" />
              </div>

              <div class="cert-center-logo">
                <img src="/QGC/ICI 글로벌 로고.png" alt="ICI Global Logo" class="center-logo-img" />
              </div>
              <div class="cert-empty"></div>
            </div>

            <!-- 2행: 왼쪽 / 오른쪽 사인 -->
            <div class="cert-signatures">
              <div class="sig-block">
                <img src="/QGC/sign1.jpg" class="sig-img" alt="Signature 1" />
                <div class="sig-name">Chief Executive Officer</div>
                <div class="sig-org">Internal Control Institute</div>
              </div>

              <div class="sig-block">
                <img src="/QGC/sign2.png" class="sig-img" alt="Signature 2" />
                <div class="sig-name">President</div>
                <div class="sig-org">Internal Control Institute</div>
              </div>
            </div>
          </div>

        </div>

          <!-- =========================
            2페이지: 평가 현황 리포트
          ========================= -->
        <!-- 2페이지 -->
        <div class="certificate-card eval-card" ref="page2">
          <div class="eval-page">
            <p class="eval-issue">발급번호 {{ state.issueCode }}</p>
            <!-- 1) 평가분야별 현황 (모든 도메인 요약) -->
            <section class="eval-section">
              <div class="eval-section-title-bar">평가분야별 현황</div>

              <table class="eval-summary-table">
                <thead>
                  <tr>
                    <th>연도</th>
                    <th
                      v-for="domain in domainStandardList"
                      :key="domain.domainCode"
                    >
                      {{ domain.domainName.replace(/\(.*\)/g, '').trim() }}
                    </th>
                    <th>종합</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="domainStandardList.length">
                    <td>{{ domainStandardList[0].orgGiasYear }}</td>
                    <td
                      v-for="domain in domainStandardList"
                      :key="domain.domainCode"
                    >
                      {{ Number(domain.domainScore1).toFixed(1) }} {{ domain.domainGrade }}
                    </td>
                    <td>
                      {{ Number(domainStandardList[0].finalScore).toFixed(1) }}
                      {{ domainStandardList[0].finalGrade }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </section>

            <!-- 2) 평가분야 원칙별 세부현황: 도메인 A,1,2만 -->
            <section class="eval-section">
              <div class="eval-section-title-bar">평가분야 원칙별 세부현황</div>

              <div
                v-for="domain in domainPage2"
                :key="domain.domainCode"
                class="eval-subsection"
              >
                <div class="eval-subsection-header">
                  <div class="eval-dot"></div>
                  <div class="eval-subsection-title">
                    {{ domain.domainName.replace(/\(.*\)/g, '').trim() }}
                  </div>
                  <div class="eval-year-badge">
                    {{ domain.orgGiasYear }}년
                  </div>
                </div>

                <table class="eval-detail-table">
                  <tbody>
                    <tr
                      v-for="standard in domain.standards"
                      :key="standard.standardCode"
                    >
                      <td class="eval-item-code">{{ standard.standardCode }}</td>
                      <td class="eval-item-text">{{ standard.standardName }}</td>
                      <td class="eval-score">{{ standard.standardScore1 }}</td>
                      <td class="eval-grade">{{ standard.standardGrade }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </div>

        <!-- 3페이지 -->
        <div class="certificate-card eval-card" ref="page3">
          <div class="eval-page">
            <p class="eval-issue">발급번호 {{ state.issueCode }}</p>
            <section class="eval-section">
              <div class="eval-section-title-bar">평가분야 원칙별 세부현황</div>

              <div
                v-for="domain in domainPage3"
                :key="domain.domainCode"
                class="eval-subsection"
              >
                <div class="eval-subsection-header">
                  <div class="eval-dot"></div>
                  <div class="eval-subsection-title">
                    {{ domain.domainName.replace(/\(.*\)/g, '').trim() }}
                  </div>
                  <div class="eval-year-badge">
                    {{ domain.orgGiasYear }}년
                  </div>
                </div>

                <table class="eval-detail-table">
                  <tbody>
                    <tr
                      v-for="standard in domain.standards"
                      :key="standard.standardCode"
                    >
                      <td class="eval-item-code">{{ standard.standardCode }}</td>
                      <td class="eval-item-text">{{ standard.standardName }}</td>
                      <td class="eval-score">{{ standard.standardScore1 }}</td>
                      <td class="eval-grade">{{ standard.standardGrade }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </div>


        <!-- 4페이지 : 품질등급의 정의 -->
        <div class="certificate-card eval-card" ref="page4">
          <div class="eval-page grade-def-page">
            <!-- 발급번호 -->
            <p class="eval-issue">발급번호 {{ domainStandardList[0]?.issueCode || state.issueCode }}</p>

            <!-- 섹션 타이틀 -->
            <section class="eval-section">
              <div class="eval-section-title-bar">품질등급의 정의</div>

              <table class="grade-def-table">
                <tbody>
                  <!-- A 구간 -->
                  <tr>
                    <td class="range-cell">90~</td>
                    <td class="level-cell" rowspan="2">매우 충족</td>
                    <td class="grade-cell">A+</td>
                    <td class="desc-cell" rowspan="2">
                      각 분야별 원칙에 대해, 내부감사활동은 국제내부감사 기준에 대해
                      중요성 측면에서 모두 준수하고 있음.
                    </td>
                  </tr>
                  <tr>
                    <td class="range-cell">80–89</td>
                    <td class="grade-cell">A-</td>
                  </tr>

                  <!-- B 구간 -->
                  <tr>
                    <td class="range-cell">70–79</td>
                    <td class="level-cell" rowspan="2">충족</td>
                    <td class="grade-cell">B+</td>
                    <td class="desc-cell" rowspan="2">
                      각 분야별 원칙에 대해, 내부감사활동은 국제내부감사 기준에 대해
                      전반적으로 준수하고 있으며, 일부 개선 여지가 있음.
                    </td>
                  </tr>
                  <tr>
                    <td class="range-cell">60–69</td>
                    <td class="grade-cell">B-</td>
                  </tr>

                  <!-- C 구간 -->
                  <tr>
                    <td class="range-cell">50–59</td>
                    <td class="level-cell" rowspan="2">개선 여지</td>
                    <td class="grade-cell">C+</td>
                    <td class="desc-cell" rowspan="2">
                      각 분야별 원칙에 대해, 국제내부감사 기준의 준수를 위해 상당한 노력을
                      하고 있으나 중요한 목표를 충분히 달성하지 못하고 있음.
                    </td>
                  </tr>
                  <tr>
                    <td class="range-cell">40–49</td>
                    <td class="grade-cell">C-</td>
                  </tr>

                  <!-- D 구간 -->
                  <tr>
                    <td class="range-cell">30–39</td>
                    <td class="level-cell" rowspan="2">취약</td>
                    <td class="grade-cell">D+</td>
                    <td class="desc-cell" rowspan="2">
                      각 분야별 원칙에 대해, 국제내부감사 기준에 대한 준수 수준이 낮아
                      핵심 목표 달성을 저해하고 있음.
                    </td>
                  </tr>
                  <tr>
                    <td class="range-cell">20–29</td>
                    <td class="grade-cell">D-</td>
                  </tr>

                  <!-- E 구간 -->
                  <tr>
                    <td class="range-cell">10–19</td>
                    <td class="level-cell" rowspan="2">매우 취약</td>
                    <td class="grade-cell">E+</td>
                    <td class="desc-cell" rowspan="2">
                      각 분야별 원칙에 대해, 내부감사활동은 국제내부감사 기준에 대해
                      거의 또는 전혀 준수하지 못하고 있으며,
                      그 목표 달성을 위한 실질적인 노력이 부족한 상태임.
                    </td>
                  </tr>
                  <tr>
                    <td class="range-cell">~9</td>
                    <td class="grade-cell">E-</td>
                  </tr>
                </tbody>
              </table>
            </section>
          </div>

          <div class="cert-center-logo2">
                <img src="/QGC/ICI 글로벌 로고.png" alt="ICI Global Logo" class="center-logo-img" />
              </div>
          </div>

        <!-- 페이지 분할 끝 -->
      </div>

    </div>

  </div>
</template>

<script setup>
import { useStore } from 'vuex'
import { computed,onMounted,ref, watch ,reactive ,nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import PdfEmbed from "vue-pdf-embed";
import { selectLatestCertificateByOrg,updateCertAjax,updateDownloadCount } from '@/api/org'
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// npm install html2canvas jspdf

const certificateRef = ref(null);

const checkUserId = computed(() => store.state.temp.userInfo )
const route = useRoute();
const router = useRouter()
const store = useStore()

const domainList = computed(() => store.state.grid.certGiasData);// 도메인 데이터 가져오기

const domainStandardList = computed(() => {
  const map = new Map();
  console.log(domainList.value.length)
  // domainList.value 가 배열이라고 가정
  for (const item of domainList.value || []) {
    if (!item.domainCode) continue; // 방어 코드
    console.log(item.domainCode)
    // 1) 도메인 레벨 생성/조회
    if (!map.has(item.domainCode)) {
      map.set(item.domainCode, {
        domainCode: item.domainCode,
        domainName: item.domainName,
        domainScore1 : item.domainScore1,
        domainGrade : item.domainGrade,
        orgGiasYear : item.orgGiasYear,
        finalScore : item.finalScore,
        finalGrade : item.finalGrade,
        standards: []
      });
    }
    const domain = map.get(item.domainCode);

    // 2) 스탠다드 레벨 추가 (중복 제거)
    if (
      item.standardCode &&
      !domain.standards.some(s => s.standardCode === item.standardCode)
    ) {
      domain.standards.push({
        standardCode: item.standardCode,
        standardName: item.standardName,
        standardScore1: item.standardScore1,     // ★ 실제 필드명에 맞게 수정 (예: item.standardScore)
        standardGrade: item.standardGrade      // ★ 실제 필드명에 맞게 수정 (예: item.standardGrade)
      });
    }
  }

  // Map → Array 로 변환
  return Array.from(map.values());
});

const domainPage2 = computed(() =>
  domainStandardList.value.filter(d =>
    ['A', '1', '2'].includes(d.domainCode)
  )
);

const domainPage3 = computed(() =>
  domainStandardList.value.filter(d =>
    ['3', '4'].includes(d.domainCode)
  )
);


let orgName = ref('')
let orgCode = ref('')

// ① 모달 보이기/숨기기
const showModal = ref(false)

// ② 모달 안에서 입력할 값
const form = ref({
  businessNumber: '',
  address: '',
  standingAuditorName : '',
  expireDate : '',
  evaluationDate : ''
})

// 모달 저장 → 인증서 화면으로 이동
const saveModal = async () => {
  if (!form.value.businessNumber || !form.value.address || !form.value.standingAuditorName) {
    alert("사업자번호와 주소를 입력하세요!")
    return
  }

  if (!confirm("입력하신 기관 정보를 저장하시겠습니까?")) return

  let params = {
    id : state.id,
    businessNumber : form.value.businessNumber,
    address : form.value.address,
    standingAuditorName : form.value.standingAuditorName
  }

  const result = await updateCertAjax(params)
  if (result?.result === 'ok') {
    alert("기관 정보가 저장되었습니다.");

    // ② 저장 후 최신 인증서 정보 재조회 (★핵심)
    const refreshed = await selectLatestCertificateByOrg({
      organizationCode: orgCode.value
    });

    if (refreshed?.result === 'ok') {
      state.businessNumber = refreshed.data.businessNumber;
      state.address = refreshed.data.address;
      state.standingAuditorName = refreshed.data.standingAuditorName;

      state.evaluationDate = refreshed.data.evaluationDate;
      state.expireDate = refreshed.data.expireDate;

      state.status = refreshed.data.status;
      state.orgInfoCompleted = refreshed.data.orgInfoCompleted;
    }

    // ③ 모달 닫기
    showModal.value = false;

  } else {
    alert(result?.resultMsg || "저장 중 오류가 발생했습니다.");
  }
}

const state = reactive({
  id : '',
  organizationCode : '',
  issueCode : '',
  grade : '',
  evaluationDate : '',
  expireDate : '',
  standingAuditorName : '',
  businessNumber : '',
  address : '',
  certificateVisible : '',
  status : '',
  orgInfoCompleted : '',
  createdAt : '',
  downloadCount : 0
})

// 점수 조회
const getEvlData = async () => {
  let param = {
    organizationCode: orgCode.value,
    orgGiasYear : '2025'
  };
  //await store.dispatch("grid/fetchAdminEvlOrg2", param); fetchSelectGiasWeight
  await store.dispatch("grid/fetchCertGias", param);
};

onMounted(async() => {
  orgName.value = checkUserId.value?.organizationName || ''; 
  orgCode.value = checkUserId.value?.organizationCode || ''; 

  const result = await selectLatestCertificateByOrg({ organizationCode : orgCode.value });

  if (result?.result === 'ok') {
    state.id = result.data.id
    state.organizationCode = result.data.organizationCode
    state.issueCode = result.data.issueCode
    state.grade = result.data.grade
    state.evaluationDate = result.data.evaluationDate
    state.expireDate = result.data.expireDate
    state.standingAuditorName = result.data.standingAuditorName
    state.businessNumber = result.data.businessNumber
    state.address = result.data.address
    state.certificateVisible = result.data.certificateVisible
    state.status = result.data.status
    state.orgInfoCompleted  = result.data.orgInfoCompleted
    state.createdAt = result.data.createdAt,
    state.downloadCount = result.data.downloadCount
  }

  const needOrgInfo =
    !state.standingAuditorName ||
    !state.businessNumber ||
    !state.address

  if (needOrgInfo) {
    // 하나라도 비어 있으면 모달 오픈
    showModal.value = true
  } else {
    // 다 채워져 있으면 모달 없이 바로 화면
    showModal.value = false

    // 화면에 바인딩되는 form에도 값 세팅 (테이블에서 form.* 쓰고 있으니까)
    form.value.standingAuditorName = state.standingAuditorName
    form.value.businessNumber      = state.businessNumber
    form.value.address         = state.address
    form.value.expireDate         = state.expireDate
    form.value.evaluationDate         = state.evaluationDate
  }
  await getEvlData()
});

const formatDate = (dateStr) => {
  if (!dateStr) return '';

  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: '2-digit'
  });
};


const page1 = ref(null);
const page2 = ref(null);
const page3 = ref(null);
const page4 = ref(null);

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const downloadPdf = async (id) => {
  try {

    if(state.downloadCount >= 3){
      alert("다운로드 휫수를 초과했습니다.")
      return false
    }
    let param = {
			id : id,
		}
    let rtn = await updateDownloadCount( param );

    if (rtn?.result != 'ok') {
			return false
		} 

    state.downloadCount = (state.downloadCount || 0) + 1;
    
    // 1) Vue가 화면 업데이트 다 끝낼 때까지 대기
    await nextTick();

    // 2) 웹폰트 로딩 완료까지 대기 (지원 브라우저에서)
    if ('fonts' in document) {
      await document.fonts.ready;
    }

    // 3) 느린 서버 고려해서 조금 더 기다리기
    await sleep(300);

    const targets = [
      page1.value,
      page2.value,
      page3.value,
      page4.value,   // 4페이지까지 있으면 포함
    ].filter(Boolean);

    if (!targets.length) {
      alert('PDF로 변환할 페이지를 찾지 못했습니다.');
      return;
    }

    const pdf = new jsPDF({
      orientation: 'p',
      unit: 'mm',
      format: 'a4',
    });

    for (let i = 0; i < targets.length; i++) {
      const el = targets[i];

      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff', // 혹시 모를 배경색 문제 방지
      });

      const imgData = canvas.toDataURL('image/png');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let posY = 0;
      if (imgHeight < pdfHeight) {
        posY = (pdfHeight - imgHeight) / 2;
      }

      if (i !== 0) {
        pdf.addPage();
      }

      pdf.addImage(imgData, 'PNG', 0, posY, imgWidth, imgHeight);
    }

    // 마지막 페이지까지 추가하고 나서
    const org = orgName.value;
    const year = domainStandardList.value[0]?.orgGiasYear || new Date().getFullYear();

    const fileName = `내부감사_품질인증서_${org}_${year}.pdf`;

    pdf.save(fileName);
    //pdf.save('internal-audit-certificate.pdf');
  } catch (e) {
    console.error('PDF 생성 중 오류:', e);
    alert('PDF 생성 중 오류가 발생했습니다. 콘솔 로그를 확인해주세요.');
  }
};

</script>

<style scoped>
.modal-title {
  font-size: 22px;  /* 기존보다 작게 */
  font-weight: bold;
  margin-bottom: 20px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-box {
  background: #fff;
  padding: 25px 30px;
  width: 400px;
  border-radius: 10px;
}

.modal-row {
  display: flex;
  flex-direction: column;
  margin-bottom: 15px;
}

.modal-row label {
  font-weight: bold;
  margin-bottom: 5px;
}

.modal-row input {
  padding: 10px;
  border: 1px solid #bbb;
  border-radius: 6px;
}

.modal-save {
  width: 100%;
  padding: 12px;
  background: #1F3A72;
  color: white;
  border: none;
  border-radius: 6px;
}

/***디자인2 
src:  url('../font/icon-font.eot?c2y60d');
*/

.cert-qc-title {
  font-family: 'OldEnglish';
  font-size: 42px;
  letter-spacing: 1px;
}



.certificate-page {
  display: flex;
  flex-direction: column;  /* 위아래로 쌓기 */
  align-items: center;
  padding: 40px 0;
  background: #f2f2f2;
}

.certificate-card {
  width: 820px;
  min-height: 1100px;
  background: #fff;
  padding: 40px 50px 60px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
  border: 1px solid #c0c0c0;
  box-sizing: border-box;
}

/* 상단 타이틀 */
.cert-title {
  font-family: "Times New Roman", Times, serif;
  text-align: center;
  font-size: 41px;
  font-weight: bold; /* 또는 700 */
  margin: 0;
  margin-bottom: 12px;
}

/* 파란 띠 */
.cert-blue-bar {
  background: #1f3a72;
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  text-align: center;
  padding: 6px 8px;
  margin-bottom: 24px;
}

/* Certificate Number */
.cert-number {
  font-size: 16px;
  margin-bottom: 18px;
  padding-left: 20px; /* 조절 가능 */
}
.cert-number-text {
  display: inline-block;              /* inline 요소라도 border 적용되도록 */
  border-bottom: 3px solid #1f3a72;   /* 텍스트만 밑줄 */
  padding-bottom: 1px;                /* 밑줄과 텍스트 간격 */
}
.cert-number .label {
  font-weight: 700;
  font-size: 23px;
  font-family: "Times New Roman", Times, serif;
}

.cert-number .value {
  font-weight: 700;
  font-size: 23px;
  font-family: "Times New Roman", Times, serif;
}

/* 설명 문단 */
.cert-text {
  font-family: "Times New Roman", Times, serif;
  font-size: 18px;
  line-height: 1.6;
  font-weight: 500;
  text-align: center;
  margin-bottom: 26px;
}

.cert-text .highlight {
  font-weight: 700;
  color: #1f3a72;
  transform: skewX(-8deg);        /* 살짝 기울기 (휘어보이게) */
  font-style: italic;             /* Italic */
}

/* 메인 row: 테이블 + 등급 */
.cert-main-row {
  display: flex;
  gap: 24px;
  align-items: stretch;
  margin-bottom: 30px;
}

/* 왼쪽 테이블 */
.cert-table {
  font-family: "Times New Roman", Times, serif;
  flex: 2;
  font-weight: bold; /* 또는 700 */
  border-collapse: collapse;
  font-size: 13px;
}

.cert-table th,
.cert-table td {
  border: 1px solid #1f3a72;
  padding: 10px 12px;
}

.cert-table th {
  width: 40%;
  background: #e0e0e0;
  font-weight: 700;
  text-align: left;
}

.cert-table td {
  width: 60%;
}

/* 오른쪽 등급 박스 */
.cert-grade-box {
  flex: 1;
  border: 2px solid #1f3a72;
  display: flex;
  flex-direction: column;
}

.cert-grade-header {
  font-family: "Times New Roman", Times, serif;
  background: #1f3a72;
  color: #fff;
  padding: 10px;
  font-size: 14px;
  font-weight: 700;
  text-align: center;
}

.cert-grade-value {
  font-family: "old Face", Times, serif;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 72px;
  font-weight: 700;
  color: #1f3a72;
  position: relative;      /* ✔ 기준 컨테이너로 만들어줌 */
  overflow: hidden;        /* ✔ 혹시 삐져나오는 부분 잘라내기 */
  z-index: 1000;
}

.grade-watermark {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%); /* 중앙 정렬 */
  width: 100%;
  height: 100%;
  opacity: 0.25;          /* 워터마크 느낌 */
  pointer-events: none;   /* 마우스 이벤트 통과 */
  /* z-index는 일단 빼거나 0 정도로 두기 */
  z-index: -1;
}


/* 중간 타이틀 영역 */
.cert-middle {
  text-align: center;
  margin-top: 10px;
  margin-bottom: 16px;
}

.cert-middle > * {
  margin-top: 8px;
  margin-bottom: 2px;
}

.cert-issued {
  font-family: "Times New Roman", Times, serif;
  font-size: 25px;
  font-weight: 600;
}

.cert-issued-sub {
  font-family: "Times New Roman", Times, serif;
  font-size: 15px;
  margin-top: 8px;
}

/* 본문 설명 */
.cert-body-text {
  font-family: "Times New Roman", Times, serif;
  text-align: center;
  font-weight: 500; /* 또는 700 */
  font-size: 18px;
  line-height: 1.6;
  margin-top: 12px;
  margin-bottom: 36px;
}

.logo-placeholder {
  display: inline-block;
  font-size: 12px;
  padding: 6px 10px;
  border: 1px dashed #aaa;
  border-radius: 4px;
}

.sig-block {
  width: 100%;
  text-align: center;
}

.sig-line {
  border-bottom: 1px solid #000;
  margin-bottom: 4px;
  height: 40px; /* 실제 서명 이미지 들어갈 자리 느낌 */
}

.sig-name {
  font-weight: 600;
  font-size: 13px;
}

.sig-org {
  font-size: 11px;
}

/* cert-table 첫 번째 행 위쪽에 5px 파란 테두리 */
.cert-table tr:first-child th,
.cert-table tr:first-child td {
  border-top: 5px solid #1f3a72 !important;
}

/* cert-table 마지막 행 아래쪽에 5px 파란 테두리 */
.cert-table tr:last-child th,
.cert-table tr:last-child td {
  border-bottom: 5px solid #1f3a72 !important;
}


/* 왼쪽 금박딱지 */
.cert-seal img {
  width: 140px;
  height: auto;
}

/* 중앙 로고 */
.center-logo-img {
  width: 150px;
  height: auto;
}

/* 서명 이미지 */
.sig-img {
  width: 160px;
  height: auto;
  margin-bottom: 5px;
}

/* 하단 전체 영역: 위/아래 두 줄 */
.cert-bottom {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* 1행: 금박딱지 + 중앙 로고 */
.cert-bottom-top {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr; /* 좌 / 중앙 / 우 */
  align-items: center;
}

/* 가운데 로고는 중앙 정렬 */
.cert-center-logo {
  display: flex;
  justify-content: center;
}

/* 가운데 로고는 중앙 정렬 */
.cert-center-logo2 {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

/* 빈 공간 */
.cert-empty {
  width: 140px; /* 금박 이미지 너비와 균형 맞춤 */
}

/* 왼쪽 금박딱지 */
.cert-seal img {
  width: 140px;
  height: auto;
}

/* 중앙 로고 */
.center-logo-img {
  width: 160px;
  height: auto;
}

/* 2행: 사인 2개를 양쪽으로 배치 */
.cert-signatures {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

/* 서명 이미지 */
.sig-img {
  width: 250px;
  height: auto;
  margin-bottom: 8px;
}

.sig-block {
  text-align: center;
}

.cert-download-btn {
  padding: 8px 16px;
  background-color: #1f3a72;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
}

.cert-download-btn:hover {
  opacity: 0.9;
}

.curved-bold {
  font-weight: 700;               /* Bold */
  font-style: italic;             /* Italic */
  font-family: "Times New Roman", serif;
  display: inline-block;
  transform: skewX(-8deg);        /* 살짝 기울기 (휘어보이게) */
  letter-spacing: 0.3px;
}


/** 스탠다드 정보 출력 css */

/* ================
   2페이지 평가 리포트
   ================ */

.eval-page {
  margin-top: 40px;
  font-family: "Malgun Gothic", "맑은 고딕", "Noto Sans KR", sans-serif;
  font-size: 12px;
}

/* 상단 발급번호 */
.eval-issue {
  font-size: 12px;
  font-weight: 700;      /* Bold */
  color: #333;
  margin-bottom: 28px;
  padding-bottom: 6px;   /* 밑줄과의 간격 */
  border-bottom: 2px solid #d0d0d0; /* 회색 줄 */
}


/* 섹션 제목 (파란 띠) */
.eval-section {
  margin-top: 16px;
}

.eval-section-title-bar {
  background-color: #1f3a72;
  color: #fff;
  font-weight: 700;
  padding: 6px 10px;
  font-size: 13px;
  margin-bottom: 8px;
}

/* 평가분야별 현황 테이블 */
.eval-summary-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
}

.eval-summary-table th,
.eval-summary-table td {
  border: 1px solid #b5c7e7;
  padding: 6px 4px;
  text-align: center;
}

.eval-summary-table thead th {
  background-color: #e7effc;
  font-weight: 700;
  color: #1f3a72;
}

/* 원칙별 세부현황 - 소제목 */
.eval-subsection {
  margin-top: 18px;
}

.eval-subsection-header {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.eval-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #1f3a72;
  margin-right: 6px;
}

.eval-subsection-title {
  font-weight: 700;
  font-size: 12px;
}

.eval-year-badge {
  margin-left: auto;
  background-color: #e7e7e7;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 700;
}

/* 원칙별 상세 테이블 */
.eval-detail-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
}

.eval-detail-table td {
  border-bottom: 1px solid #b5c7e7;
  padding: 4px 4px;
}

.eval-item-code {
  width: 40px;
  color: #1f3a72;
  font-weight: 600;
}

.eval-item-text {
  text-align: left;
}

.eval-score {
  width: 60px;
  text-align: right;
  padding-right: 8px;
  color: #1f3a72;
}

.eval-grade {
  width: 40px;
  text-align: center;
  font-weight: 700;
}

.certificate-card {
  width: 820px;
  min-height: 1100px;
  background: #fff;
  padding: 30px 50px 60px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
  border: 1px solid #c0c0c0;
  box-sizing: border-box;
}

.eval-card {
  margin-top: 40px;
  padding: 3px 50px 60px;
}

/* 2, 3페이지용 카드(리포트 페이지)만 살짝 키우기 */
.certificate-card.eval-card {
  padding: 5px 60px 70px; /* 상하/좌우 여백 조금 더 */
}

/* 2, 3페이지 기본 폰트/행간 업 */
.eval-page {
  font-size: 13px;          /* 기존 12였다면 13~14 정도 추천 */
  line-height: 1.7;         /* 행간 조금 넉넉하게 */
}
.eval-section-title-bar {
  font-size: 14px;          /* 13 → 14 */
  padding: 8px 16px;        /* 위아래/좌우 패딩 증가 */
}

.eval-summary-table {
  font-size: 12px;          /* 11 → 12 정도 */
}

.eval-summary-table th,
.eval-summary-table td {
  padding: 8px 6px;         /* 행 높이 늘리기 */
}
.eval-detail-table {
  font-size: 12px;      /* 기본 11 → 12 */
}

.eval-detail-table td {
  padding: 2px 2px;     /* 행간 확보 */
}

/* 코드/점수/등급 칸도 조금 여유 있게 */
.eval-item-code {
  width: 45px;          /* 40 → 55 */
}

.eval-score {
  width: 70px;
  text-align: right;
  padding-right: 10px;
}

.eval-grade {
  width: 45px;
}
.eval-subsection {
  margin-top: 22px;       /* 섹션 사이 여백 조금 더 */
}

.eval-subsection-title {
  font-size: 13px;        /* 12 → 13 */
}

.eval-year-badge {
  font-size: 12px;        /* 11 → 12 */
  padding: 4px 12px;
}

/* 4페이지 : 품질등급 정의 페이지 약간 여유 있게 */
/* 4페이지 전체를 조금 더 크게 보이게 */
.grade-def-page {
  font-size: 14px;      /* 13 -> 14 */
  line-height: 1.9;     /* 행간 넉넉하게 */
}

/* 등급 정의 테이블을 세로로 꽉 차게 보이게 */
.grade-def-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  font-size: 13px;      /* 셀 안 글자도 살짝 키움 */
}

/* 각 행 높이/패딩 크게 */
.grade-def-table tr {
  height: 70px;         /* ★ 행 자체 높이 증가 (필요하면 60~80 사이로 조절) */
}

.grade-def-table td {
  border: 1px solid #b5c7e7;
  padding: 14px 10px;   /* 위·아래 패딩 늘리기 (원래 8px 정도였을 거야) */
  vertical-align: middle;
}

/* 점수 범위 셀 */
.range-cell {
  width: 80px;          /* 폭도 조금 넓게 */
  text-align: center;
  font-weight: 600;
}

/* 수준(매우 충족/충족/...) */
.level-cell {
  width: 90px;
  text-align: center;
  font-weight: 700;
  background-color: #e7effc;
  color: #1f3a72;
}

/* 등급(A+/A-/...) */
.grade-cell {
  width: 55px;
  text-align: center;
  font-weight: 700;
  font-size: 14px;
}

/* 설명문 */
.desc-cell {
  text-align: left;
  padding-left: 14px;
  padding-right: 8px;
}

.grade-def-table tr {
  height: 80px;
}
</style>
